/**
 * RitualCollapsibles Component
 * Collapsible sections for What You'll Need, What You'll Experience,
 * Step-by-Step Guidance, and Intention
 */

import { Collapsible } from "@/components/ui/collapsible"
import { useTranslation } from "@/language"
import type { RitualStep } from "../../types/detail.types"

interface WhatYoullNeedProps {
  items: string[]
  defaultOpen?: boolean
}

export function WhatYoullNeedSection({ items, defaultOpen: defaultOpenProp }: WhatYoullNeedProps) {
  const { t } = useTranslation("rituals")

  const resolvedDefaultOpen = defaultOpenProp ?? items.length > 0

  if (items.length === 0) {
    return (
      <Collapsible
        title={t("ritualAccordion.whatYouNeed")}
        defaultOpen={defaultOpenProp ?? false}
        className="text-primary leading-5.5 border-[#FFFFFF1A] bg-[#FFFFFF1A]"
      >
        <p className="text-primary text-sm">{t("ritualAccordion.noItems")}</p>
      </Collapsible>
    )
  }

  return (
    <Collapsible
      title={t("ritualAccordion.whatYouNeed")}
      defaultOpen={resolvedDefaultOpen}
      className="text-primary leading-5.5 border-[#FFFFFF1A] bg-[#FFFFFF1A]"
    >
      <div className="space-y-2">
        {items.map(item => (
          <div key={item} className="text-primary leading-5.5 text-sm flex items-start gap-2">
            <span>{item}</span>
          </div>
        ))}
      </div>
    </Collapsible>
  )
}

interface WhatYoullExperienceProps {
  items: string[]
  defaultOpen?: boolean
}

export function WhatYoullExperienceSection({
  items,
  defaultOpen: defaultOpenProp,
}: WhatYoullExperienceProps) {
  const { t } = useTranslation("rituals")

  const resolvedDefaultOpen = defaultOpenProp ?? items.length > 0

  if (items.length === 0) {
    return (
      <Collapsible
        title={t("ritualAccordion.whatYouExperience")}
        defaultOpen={defaultOpenProp ?? false}
        className="leading-5.5 text-primary border-[#FFFFFF1A] bg-[#FFFFFF1A]"
      >
        <p className="text-primary leading-5.5 text-sm">{t("ritualAccordion.detailsComing")}</p>
      </Collapsible>
    )
  }

  return (
    <Collapsible
      title={t("ritualAccordion.whatYouExperience")}
      defaultOpen={resolvedDefaultOpen}
      className="leading-5.5 text-primary border-[#FFFFFF1A] bg-[#FFFFFF1A]"
    >
      <div className="space-y-2">
        {items.map(item => (
          <div key={item} className="text-primary leading-5.5 text-sm flex items-start gap-2">
            <span>{item}</span>
          </div>
        ))}
      </div>
    </Collapsible>
  )
}

interface StepByStepGuidanceProps {
  steps: RitualStep[]
  canAccess?: boolean
  defaultOpen?: boolean
}

export function StepByStepGuidanceSection({
  steps,
  canAccess = true,
  defaultOpen = true,
}: StepByStepGuidanceProps) {
  const { t } = useTranslation("rituals")

  return (
    <Collapsible title={t("ritualAccordion.steps")} defaultOpen={defaultOpen}>
      {canAccess ? (
        <div className="space-y-4">
          {steps.map(step => (
            <div key={step.number} className="text-sm leading-5.5">
              <span className="text-primary">
                <span className="text-stone">{step.description}</span>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="relative">
          {/* Blurred content */}
          <div className="space-y-4 blur-sm select-none pointer-events-none" aria-hidden="true">
            {steps.map(step => (
              <div key={step.number} className="text-sm leading-5.5">
                <span className="text-primary">
                  <span className="text-stone">{step.description}</span>
                </span>
              </div>
            ))}
          </div>
          {/* Lock overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center bg-card/90 px-4 py-2 rounded-lg">
              <p className="text-sm text-primary font-medium leading-5.5">
                {t("ritualAccordion.joinForGuidance")}
              </p>
            </div>
          </div>
        </div>
      )}
    </Collapsible>
  )
}

interface IntentionSectionProps {
  intention?: string
  intentions?: Array<{ id: string; name: string }>
  defaultOpen?: boolean
}

export function IntentionSection({
  intention,
  intentions,
  defaultOpen = true,
}: IntentionSectionProps) {
  const { t } = useTranslation("rituals")

  const hasIntentionChips = intentions && intentions.length > 0
  const hasIntentionText = !hasIntentionChips && intention

  if (!hasIntentionChips && !hasIntentionText) return null

  return (
    <Collapsible
      title={t("ritualAccordion.intention")}
      defaultOpen={defaultOpen}
      className="border-[#FFFFFF1A] bg-[#FFFFFF1A]"
    >
      {hasIntentionChips ? (
        <div className="flex flex-wrap gap-2.5">
          {(intentions ?? []).map(i => (
            <span
              key={i.id}
              className="text-[13px] leading-[20px] border border-text-primary rounded-md px-2.5 py-2 text-text-primary whitespace-nowrap"
            >
              {i.name}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[#F2F0EBE6] text-sm leading-5.5">{intention}</p>
      )}
    </Collapsible>
  )
}
